import { z } from "zod";

// Projects scope analyses, campaigns, email accounts and filters to a client
// or sub-business. Prospects and the account finder are not project-scoped.

export const projectSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  isDefault: z.boolean(),
  /** Archived instead of deleted (S2.06): hidden by default, its data stays reachable. */
  archived: z.boolean(),
});
export type Project = z.infer<typeof projectSchema>;

export const listProjectsInputSchema = z.object({
  /** Shows archived projects too; default projects list excludes them (S2.06). */
  includeArchived: z.boolean().optional(),
});
export type ListProjectsInput = z.infer<typeof listProjectsInputSchema>;

export const projectListSchema = z.object({
  projects: z.array(projectSchema),
  currentProjectId: z.string(),
  /** The plan's project limit; null when unlimited. Archived projects do not count
   * against it (S2.06): archiving one frees the slot it held. */
  max: z.number().int().nonnegative().nullable(),
});
export type ProjectList = z.infer<typeof projectListSchema>;

/** The longest project name, shown on fields as their limit. */
export const PROJECT_NAME_MAX = 20;

export const projectNameSchema = z.string().trim().min(1).max(PROJECT_NAME_MAX);

export const createProjectInputSchema = z.object({ name: projectNameSchema });
export const renameProjectInputSchema = z.object({ projectId: z.string(), name: projectNameSchema });
export const archiveProjectsInputSchema = z.object({ projectIds: z.array(z.string()).min(1) });
export const unarchiveProjectInputSchema = z.object({ projectId: z.string() });
export const selectProjectInputSchema = z.object({ projectId: z.string() });
export const emptyInputSchema = z.object({});

export const archiveProjectsOutputSchema = z.object({
  archived: z.array(z.string()),
  /** Names of the projects that could not be archived, with the reason code. */
  failed: z.array(z.object({ name: z.string(), code: z.string() })),
});

export type CreateProjectInput = z.infer<typeof createProjectInputSchema>;
export type RenameProjectInput = z.infer<typeof renameProjectInputSchema>;
export type ArchiveProjectsInput = z.infer<typeof archiveProjectsInputSchema>;
export type ArchiveProjectsOutput = z.infer<typeof archiveProjectsOutputSchema>;
export type UnarchiveProjectInput = z.infer<typeof unarchiveProjectInputSchema>;
export type SelectProjectInput = z.infer<typeof selectProjectInputSchema>;
